import React, { useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { emailTokenVerification } from '../../Common/data/emailTokenVerification'; // API for token verification
import AuthContent from "./AuthContent";
import logoLight from "../../assets/images/logo-light.png";

const EmailVerification = () => {
    const [token, setToken] = useState("");  // State to hold the 6-digit token
    const [error, setError] = useState<string | null>(null);  // State to display error messages
    const [successMessage, setSuccessMessage] = useState<string | null>(null);  // State to display success messages
    const [isSubmitting, setIsSubmitting] = useState(false);  // State to track loading
    const navigate = useNavigate();  // For page navigation

    // Get the input element by index
    const getInputElement = (index: number): HTMLInputElement => {
        return document.getElementById('digit' + index + '-input') as HTMLInputElement;
    };

    // Move to next input when a digit is entered
    const moveToNext = (index: number) => {
        if (getInputElement(index).value.length === 1) {
            if (index !== 6) {
                getInputElement(index + 1).focus();
            } else {
                getInputElement(index).blur();
            }
            // Combine the 6 digits into the token
            const tokenValue = Array.from({ length: 6 }, (_, i) => getInputElement(i + 1).value).join("");
            setToken(tokenValue);
        }
    };

    // Handle the form submission
    const handleSubmit = async () => {
        setError(null);  // Clear any previous error
        setSuccessMessage(null);  // Clear any previous success message

        if (token.length !== 6) {
            setError("Please enter the 6-digit token.");
            return;
        }

        setIsSubmitting(true);  // Set loading state

        try {
            const response = await emailTokenVerification({ token });
            if (response.status === "success") {
                setSuccessMessage("Email verification successful! Redirecting to sign-in...");
                setTimeout(() => {
                    navigate("/signup");  // Redirect to signup after verification
                }, 3000);
            } else {
                setError(response.message || "Email verification failed.");
            }
        } catch (error: any) {
            setError("An error occurred during verification. Please try again.");
        } finally {
            setIsSubmitting(false);  // Reset loading state
        }
    };

    return (
        <React.Fragment>
            <section className="position-relative min-vh-100 d-flex align-items-center justify-content-center">
                <Container fluid>
                    <Row className="justify-content-center">
                        {window.innerWidth > 768 ? <AuthContent /> : null}
                        <Col xs={12} md={6}>
                            <div className="auth-card">
                                <Card className="border-0 mb-0">
                                    <Card.Body className="px-md-5">
                                        <img src={logoLight} height={100} alt="Logo" />
                                        <h2 className="mb-4">Verify Your Email</h2>
                                        {error && <Alert variant="danger">{error}</Alert>}
                                        {successMessage && <Alert variant="success">{successMessage}</Alert>}
                                        
                                        <Form>
                                            <div className="d-flex justify-content-between">
                                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                                    <Form.Control
                                                        key={i}
                                                        type="text"
                                                        className="form-control form-control-lg text-center mx-1"
                                                        style={{ maxWidth: '50px' }}
                                                        maxLength={1}
                                                        id={`digit${i}-input`}
                                                        onKeyUp={() => moveToNext(i)}
                                                    />
                                                ))}
                                            </div>
                                        </Form>

                                        <Button variant="primary" className="w-100 mt-4" onClick={handleSubmit} disabled={isSubmitting}>
                                            {isSubmitting ? <Spinner animation="border" size="sm" role="status" /> : "Confirm"}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="mt-4 text-center">
                                <p>Didn't receive a code? <Link to="/auth-pass-reset-basic" className="fw-semibold text-primary text-decoration-underline">Resend</Link></p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
};

export default EmailVerification;
